/**
 * Where the sun is over a place, and so which sky it has: dawn, day, dusk or night.
 *
 * Andrew, 2026-10-01, on the Location Tracking clocks: "Determine the times of day in each location
 * of which image to display." A recovery runs across sites in different parts of the world, and the
 * sky behind each clock says at a glance whether the people there are at their desks or asleep.
 *
 * From the sun's real position, not the clock's hour: sunrise in London is 04:43 in June and 08:06
 * in December, so "dawn is 06:00 to 07:00" is wrong for most of the year, and a time zone says
 * nothing about where in it a site is (Madrid and Warsaw share one). It needs only the moment and
 * the site's latitude and longitude; the time zone does not come into it.
 *
 * The position is the standard low-precision solar calculation (the Astronomical Almanac's, as
 * NOAA publishes it), good to a few hundredths of a degree for decades either side of now, which
 * is a few seconds of sunrise: far finer than a picture needs.
 */
export type SkyPhase = 'dawn' | 'day' | 'dusk' | 'night'

const RAD = Math.PI / 180
const J2000 = Date.UTC(2000, 0, 1, 12) // 2000-01-01 12:00 UTC
const norm360 = (deg: number) => ((deg % 360) + 360) % 360

export interface SunPosition {
    /** Degrees above the horizon; negative when the sun is below it. */
    elevation: number
    /** True from the sun's lowest point to its highest: the morning half of its day. */
    rising: boolean
}

export function sunPosition(at: Date, latitude: number, longitude: number): SunPosition {
    const days = (at.getTime() - J2000) / 86_400_000
    const meanAnomaly = norm360(357.529 + 0.98560028 * days) * RAD
    const meanLongitude = norm360(280.459 + 0.98564736 * days)
    const eclipticLongitude = norm360(meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.02 * Math.sin(2 * meanAnomaly)) * RAD
    const obliquity = (23.439 - 0.00000036 * days) * RAD
    const rightAscension = Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLongitude), Math.cos(eclipticLongitude)) / RAD
    const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLongitude))
    // Greenwich sidereal time in degrees, then the sun's hour angle at this longitude:
    // negative before it crosses the meridian (morning), positive after (afternoon).
    const siderealGreenwich = norm360(280.46061837 + 360.98564736629 * days)
    let hourAngle = norm360(siderealGreenwich + longitude - rightAscension)
    if (hourAngle > 180) hourAngle -= 360
    const lat = latitude * RAD
    const elevation = Math.asin(Math.sin(lat) * Math.sin(declination) + Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngle * RAD)) / RAD
    return { elevation, rising: hourAngle < 0 }
}

/**
 * How low the sun may be before the sky is night, and how high before it is plain day.
 *
 * Night begins at the end of civil twilight (6° below the horizon), when the sky is dark enough to
 * need lights. Dawn and dusk run from there to 6° above: the half hour before sunrise and the
 * forty minutes or so after it, when the sun is low and the light is coloured - what the two
 * pictures show. Above that it is day.
 */
export const NIGHT_BELOW = -6
export const DAY_ABOVE = 6

/** The sky over (latitude, longitude) at `at`. */
export function skyPhase(at: Date, latitude: number, longitude: number): SkyPhase {
    const { elevation, rising } = sunPosition(at, latitude, longitude)
    if (elevation < NIGHT_BELOW) return 'night'
    if (elevation >= DAY_ABOVE) return 'day'
    return rising ? 'dawn' : 'dusk'
}

/** A usable coordinate pair, or null. (0, 0) is in the Gulf of Guinea, and is what a blank field saves as. */
export function coordinatesOf(latitude: unknown, longitude: unknown): { latitude: number; longitude: number } | null {
    if (latitude == null || longitude == null || latitude === '' || longitude === '') return null
    const lat = Number(latitude)
    const lon = Number(longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
    if (lat === 0 && lon === 0) return null
    return { latitude: lat, longitude: lon }
}
